import React from 'react';
import AccessTime from '@material-ui/icons/AccessTime';
import type { ArrangementRequestLimitSummary } from '@this/domain/organization/arrangement_request_limit_summary';
import HelpAccordion from '@this/components/reserve_trip/dashboard/help_area_for_ait/help_accordion';
import { CATEGORY_CONFIGS, isCategoryAvailable, badgeText, UNAVAILABLE_NOTE } from './category_config';

type Props = {
  summary: ArrangementRequestLimitSummary | null;
};

const ICON_SIZE = 20;

const ArrangementRequestLimit: React.FC<Props> = ({ summary }) => {
  if (!summary) return null;

  return (
    <HelpAccordion
      title="ご依頼期限"
      icon={<AccessTime style={styles.clockIcon} />}
      rightSlot={<span style={styles.badge}>{badgeText(summary)}</span>}
    >
      <div style={styles.body}>
        <div style={styles.intro}>
          <div style={styles.introHeading}>旅程全体の依頼期限</div>
          <div style={styles.introAlert}>1つの旅程の中で、最も早い依頼期限までにご依頼ください。</div>
          <div style={styles.introNote}>
            ※ 旅行商品によって依頼期限が異なります。詳細は下表の手配カテゴリ別の依頼期限をご確認ください。
          </div>
          <div style={styles.introNote}>
            ご不明な点がございましたら、
            <a href="/inquiries/new" target="_blank" rel="noopener noreferrer" style={styles.link}>
              お問い合わせページ
            </a>
            よりご連絡ください。
          </div>
        </div>
        <div style={styles.table}>
          <div style={styles.headerRow}>
            <div style={{ ...styles.labelCell, fontWeight: 500 }}>手配カテゴリ</div>
            <div style={{ ...styles.deadlineCell, fontWeight: 500 }}>依頼期限</div>
          </div>
          {CATEGORY_CONFIGS.map(config => {
            const Icon = config.icon;
            const limit = config.getLimit(summary);
            const available =
              config.forbidden.length === 0
                ? true
                : isCategoryAvailable(config.forbidden, summary.forbiddenRepositories);
            const notes = available ? config.notes(limit, summary.premiumSupport) : [UNAVAILABLE_NOTE];
            return (
              <div key={config.id} style={available ? styles.row : { ...styles.row, ...styles.rowDisabled }}>
                <div style={styles.labelCell}>
                  <span style={styles.iconBox}>
                    <span
                      style={{
                        display: 'inline-flex',
                        transform: `scale(${ICON_SIZE / (config.iconBaseSize ?? ICON_SIZE)})`
                      }}
                    >
                      <Icon />
                    </span>
                  </span>
                  <span>{config.label}</span>
                </div>
                <div style={styles.deadlineCell}>
                  {available && (
                    <div style={styles.deadline}>{config.deadlineText(limit, summary.premiumSupport)}</div>
                  )}
                  {notes.map(note => (
                    <div key={note} style={styles.note}>
                      {note}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </HelpAccordion>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  clockIcon: {
    fontSize: 24,
    marginRight: 10
  },
  badge: {
    marginLeft: 16,
    padding: '2px 10px',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 400,
    whiteSpace: 'nowrap',
    lineHeight: '1.5'
  },
  body: {
    background: 'white',
    padding: '20px 48px 24px',
    boxShadow: '0 0 15px rgba(0, 0, 0, 0.12)'
  },
  intro: {
    margin: '0 14px 50px',
    color: '#333',
    fontSize: 13,
    fontWeight: 400
  },
  introHeading: {
    fontWeight: 500
  },
  introAlert: {
    color: '#d3380f',
    fontWeight: 500
  },
  introNote: {
    color: '#333'
  },
  link: {
    color: '#1a73e8',
    textDecoration: 'underline'
  },
  table: {
    display: 'flex',
    flexDirection: 'column'
  },
  headerRow: {
    display: 'flex',
    padding: '12px 16px',
    background: '#f5f5f5',
    color: '#333',
    borderBottom: '1px solid #e8e8e8'
  },
  row: {
    display: 'flex',
    minHeight: 68,
    padding: '0 20px',
    borderBottom: '1px solid #e8e8e8',
    color: '#3a3a3a'
  },
  rowDisabled: {
    background: '#fafafa'
  },
  labelCell: {
    flex: '0 0 280px',
    display: 'flex',
    alignItems: 'center',
    gap: 17,
    fontSize: 16,
    fontWeight: 500
  },
  iconBox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: ICON_SIZE,
    height: ICON_SIZE,
    flex: '0 0 auto',
    filter: 'brightness(0)'
  },
  deadlineCell: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    fontSize: 16
  },
  deadline: {
    color: '#1a73e8',
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'pre-line'
  },
  note: {
    color: '#777',
    fontSize: 13,
    whiteSpace: 'pre-line'
  }
};

export default ArrangementRequestLimit;
